import React, { useEffect, useState } from 'react';
import { Card, Button, DateInput } from '../../../components/common';
import { apiError } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { profitLossReportService } from '../../../services/resources';

const fmt = (n) =>
  n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 });

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + '01';

export default function ProfitLossReportPage() {
  const { can } = useAuth();
  const canView = can('REPORT_PROFIT_LOSS', 'CanView');

  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const filters = { dateFrom, dateTo };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setData(await profitLossReportService.list(filters));
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) load();
    // eslint-disable-next-line
  }, []);

  const doExport = async () => {
    setExporting(true);
    try {
      await profitLossReportService.export(filters);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setExporting(false);
    }
  };

  if (!canView) {
    return (
      <Card title="Profit & Loss Report">
        <p className="text-sm text-gray-500">You do not have permission to view this page.</p>
      </Card>
    );
  }

  const neverClosed = data?.warnings.filter((w) => w.status === 'never_closed') || [];
  const stale = data?.warnings.filter((w) => w.status === 'stale') || [];

  return (
    <div className="space-y-4">
      <Card title="Profit & Loss Report">
        <div className="flex flex-wrap items-end gap-3">
          <DateInput label="From" value={dateFrom} onChange={setDateFrom} />
          <DateInput label="To" value={dateTo} onChange={setDateTo} />
          <Button onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Apply'}
          </Button>
          <Button variant="secondary" onClick={doExport} disabled={exporting}>
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
        </div>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </Card>

      {neverClosed.length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <p className="font-semibold">Never closed periods — figures incomplete</p>
          <ul className="mt-1 list-disc pl-5">
            {neverClosed.map((w) => (
              <li key={w.period}>{w.period}: COGS not yet calculated for this period.</li>
            ))}
          </ul>
        </div>
      )}
      {stale.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <p className="font-semibold">Stale periods — closed but data changed since</p>
          <ul className="mt-1 list-disc pl-5">
            {stale.map((w) => (
              <li key={w.period}>
                {w.period}: closed {String(w.closed_on).slice(0, 10)}, data touched{' '}
                {String(w.last_touched_on).slice(0, 10)}. Consider re-closing.
              </li>
            ))}
          </ul>
        </div>
      )}

      {data && (
        <Card>
          <div className="space-y-5 text-sm">
            <section>
              <h3 className="mb-2 font-semibold text-gray-800">Gross Profit</h3>
              <table className="w-full">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-1">Currency</th>
                    <th className="py-1 text-right">Sell Revenue</th>
                    <th className="py-1 text-right">COGS</th>
                    <th className="py-1 text-right">Gross Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {data.gross_profit.map((r) => (
                    <tr key={r.currency_code} className="border-t border-gray-100">
                      <td className="py-1">
                        {r.currency_code}
                        {r.unstamped_sell_count > 0 && (
                          <span
                            title={`${r.unstamped_sell_count} SELL row(s) have no COGS stamped yet`}
                            className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700"
                          >
                            {r.unstamped_sell_count} unstamped
                          </span>
                        )}
                      </td>
                      <td className="py-1 text-right">{fmt(r.sell_revenue)}</td>
                      <td className="py-1 text-right">{fmt(r.total_cogs)}</td>
                      <td className="py-1 text-right">{fmt(r.gross_profit)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-gray-200 font-semibold text-gray-800">
                    <td className="py-1" colSpan={3}>Total Gross Profit</td>
                    <td className="py-1 text-right">{fmt(data.total_gross_profit)}</td>
                  </tr>
                </tbody>
              </table>
            </section>

            <section>
              <h3 className="mb-2 font-semibold text-gray-800">Other Income</h3>
              <table className="w-full">
                <tbody>
                  {data.other_income.map((r) => (
                    <tr key={r.income_type_name} className="border-t border-gray-100">
                      <td className="py-1">{r.income_type_name}</td>
                      <td className="py-1 text-right">{fmt(r.amount)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-gray-200 font-semibold text-gray-800">
                    <td className="py-1">Total Other Income</td>
                    <td className="py-1 text-right">{fmt(data.total_other_income)}</td>
                  </tr>
                </tbody>
              </table>
            </section>

            <section>
              <h3 className="mb-2 font-semibold text-gray-800">Other Expense</h3>
              <table className="w-full">
                <tbody>
                  {data.other_expense.map((r) => (
                    <tr key={r.expense_type_name} className="border-t border-gray-100">
                      <td className="py-1">{r.expense_type_name}</td>
                      <td className="py-1 text-right">{fmt(r.amount)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-gray-200 font-semibold text-gray-800">
                    <td className="py-1">Total Other Expense</td>
                    <td className="py-1 text-right">{fmt(data.total_other_expense)}</td>
                  </tr>
                </tbody>
              </table>
            </section>

            <div className="flex justify-between border-t-2 border-gray-300 pt-3 text-base font-bold text-gray-900">
              <span>Net Profit / Loss</span>
              <span>{fmt(data.net_profit)}</span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
